import {MuteTypeValue} from './conversation-constants.model';

/**
 * Trạng thái hội thoại — tương thích giá trị Elastic field "status" (byte).
 * Dùng const thay magic number để code dễ đọc.
 */
export const CONVERSATION_STATUS = {
  UNREAD: 1, // Chưa đọc
  READED: 2 // Đã đọc
} as const;

/**
 * Phân loại hội thoại.
 * QUAN TRỌNG: 1 là Comment, 2 là Inbox (theo bản cũ).
 */
export const CONVERSATION_TYPE = {
  COMMENT: 1,
  INBOX: 2,
  GROUP: 3,
  CHANNEL: 4
} as const;

/** Type cho giá trị status */
export type ConversationStatusValue = typeof CONVERSATION_STATUS[keyof typeof CONVERSATION_STATUS];
export type ConversationTypeValue = typeof CONVERSATION_TYPE[keyof typeof CONVERSATION_TYPE];

/**
 * Interface hội thoại — field names ĐỒNG BỘ 1:1 với Elasticsearch mapping.
 * KHÔNG map lại tên field → tránh nhầm lẫn, dễ debug, dễ bảo trì.
 *
 * Nguồn gốc: Elastic index "conversations" mapping
 * Cập nhật: 28/04/2026
 */
export interface IConversation {
  // === KHỐI ĐỊNH DANH ===
  id: string; // ID hội thoại (composite: fanpageId_fanpageUserId)
  type: number; // 1 = comment, 2 = inbox
  channel: number; // Kênh: 1=FB, 2=Zalo, 5=TikTok, 6=IG...

  // === KHỐI TRANG / SHOP ===
  fanpageId: string; // ID trang (FB PageId, Zalo OA...)
  fanpagePostId?: string; // ID bài viết (comment)
  postIds?: string[]; // Danh sách bài viết liên quan
  commentId?: string; // ID comment gốc (nếu type=2)

  // === KHỐI KHÁCH HÀNG ===
  fanpageUserId: string; // UID khách hàng trên nền tảng (PSID FB, Zalo userId...)
  fanpageUserName: string; // Tên hiển thị khách hàng
  pageUserAlias?: string; // Tên alias (Zalo cá nhân)
  customerAvatar?: string; // URL avatar khách
  hasPhone?: boolean; // Đã lưu SĐT vào CRM chưa
  customerCreatedAt?: number; // Thời điểm khách tạo (epoch_second)

  // === KHỐI TRẠNG THÁI ===
  status: number; // 1 = chưa đọc, 2 = đã đọc
  hasReply?: boolean; // Page đã trả lời hay chưa
  hasDraftOrder?: boolean; // Có đơn nháp không

  // === KHỐI TIN NHẮN & THỜI GIAN ===
  lastMessage?: string; // Giá trị tin nhắn cuối (BE ghi "2" = trả từ BE)
  lastMessageId?: string; // ID tin nhắn cuối
  message?: string; // Nội dung preview tin nhắn cuối (text hiển thị trên list)
  updatedTime: number; // Thời điểm cập nhật hội thoại (epoch_second)
  createdTime?: number; // Thời điểm tạo hội thoại (epoch_second)
  lastSentMessageTime?: number; // Thời điểm tin gửi đi cuối (epoch_second)
  lastSeenMessageTime?: number; // Thời điểm tin được seen cuối (epoch_second)
  lastSentMessageAt?: number; // Thời điểm tin gửi đi cuối (epoch_millis)
  lastSeenMessageAt?: number; // Thời điểm tin được seen cuối (epoch_millis)
  lastPageRepliedAt?: number; // Thời điểm Page trả lời cuối (epoch_millis)
  lastPageReplyAt?: number; // Thời điểm Page trả lời cuối (epoch_second)
  lastUserRepliedAt?: number; // Thời điểm KH gửi cuối (epoch_millis)
  lastUserSeenAt?: number; // Thời điểm KH seen cuối (epoch_millis)
  updatedAt?: number; // Thời điểm cập nhật (epoch_millis)
  countUnread?: number; // Số tin chưa đọc (nếu BE trả)

  // === KHỐI PHÂN LUỒNG / CHIA SỐ ===
  assigneeIds?: string[]; // Danh sách nhân viên được gán
  assignedGroupId?: string; // Nhóm được gán
  lastAssignedAt?: number; // Lần chia gần nhất (epoch_second)

  // === KHỐI GHIM & THÔNG BÁO ===
  isPinned?: boolean; // Đang ghim
  pinnedAt?: number; // Thời điểm ghim
  isMuted?: boolean; // Đang tắt thông báo
  muteType?: MuteTypeValue;
  muteUntilTimestamp?: number; // Thời điểm hết mute

  // === KHỐI NHÃN ===
  tagIds?: string[]; // Mảng ID nhãn gắn vào hội thoại

  // === KHỐI LOG ===
  logViews?: { // Thông tin lượt xem cuối
    seenTime?: number;
    /** FE13/API logviewconversation timestamp for the latest viewer. */
    createdAt?: number;
    name?: string;
    createdById?: string;
  };

  // === KHỐI FACEBOOK ===
  threadId?: string; // FB Thread ID
  threadKey?: string; // FB Thread Key
  link?: string; // Link hội thoại trên nền tảng

  // === KHỐI QUẢNG CÁO ===
  adIds?: string[]; // Danh sách ID quảng cáo
  fromAdsId?: string; // Nguồn quảng cáo

  // === KHỐI E-COMMERCE ===
  ecomBuyerId?: string; // ID buyer sàn TMĐT
  isCreatedOrder?: number; // Đã tạo đơn chưa (0/1)

  // === KHỐI CHATBOT ===
  chatbotStatus?: number | string; // Trạng thái BizAI (CHATBOT_STATUS) — BE trả số, toggle ghi chuỗi
  chatbotExpiredAt?: number; // Thời điểm hết hạn chatbot (epoch_millis)
  stopBot?: number; // Tắt bot (0/1)

  // === KHỐI KHÁC ===
  isFromLive?: number; // Đến từ livestream (0/1)
  isSentPrivateMessage?: number; // Đã gửi tin riêng (comment → inbox)
  repliedPrivate?: number; // Đã trả lời riêng
  inboxConversationId?: string | string[]; // ID hội thoại inbox, phục vụ case private reply
  settings?: any; // Cài đặt đặc thù

  // === KHỐI FE-ONLY (không từ BE, do FE gán) ===
  isPartner?: number; // 1 = hội thoại affiliate (FE gán khi merge partner)
  is_typing?: number; // > 0 = đang gõ (FE gán khi nhận socket typing event)
  isBot?: boolean; // Flag hiển thị icon Chatbot (FE gán)
  recommendedProductIds?: string[]; // SP được DO `conversation:recommend` đề xuất (FE gán)

  // === KHỐI TÌM KIẾM (FE gán) ===
  /** Kết quả tìm kiếm tin nhắn trong hội thoại này — FE13 parity: filteredSearchedData */
  filteredSearchedData?: {
    messages: ISearchMessage[];
    totalMessages: number;
  };
}

/**
 * Thông tin chi tiết khách hàng.
 * Port từ logic cũ: customer object trong showDetail.
 */
export interface ICustomerDetail {
  id: string;
  name: string;
  avatar?: string;
  phones?: string[];
  emails?: string[];
  address?: string;
  mobileNetwork?: { network: string; code?: string };
  badCustomer?: unknown[];
  fbUserId?: string;
  birthday?: number;
  gender?: number;
  isFriend?: number;
  isRequested?: number;
  isRequesting?: number;
  alias?: string;
  username?: string;

  [key: string]: unknown;
}

/** Đơn hàng lịch sử (POS / ecom) */
export interface IOrderHistoryItem {
  id: string;
  /** Mã đơn trên sàn (Shopee/Lazada/TikTok), khác với nhanhOrderId. */
  ecomOrderId?: string;
  createdAt: number;
  totalAmount: number;
  status: string;
  statusClass?: string;
  /** Tổng số lượng sản phẩm để hiển thị đúng FE13. */
  productQuantity?: number;
  /** Trạng thái đồng bộ từ sàn, nếu BE trả riêng với trạng thái Nhanh. */
  apiStatus?: string;
  /** Raw order giữ nguyên để mở chi tiết và gửi lại đúng payload sàn. */
  raw?: any;
}

/** Đơn hàng nháp */
export interface IOrderDraftItem {
  id: string;
  createdAt: number;
  source?: number;
  status?: number;
  /** Dùng để mở đúng POS khi tạo đơn Nhanh từ đơn nháp. */
  conversationId?: string;
  fanpageId?: string;
  fanpageUserId?: string;
  fanpageUserName?: string;
}

/**
 * Interface cho dữ liệu phản hồi chi tiết hội thoại.
 * Tương thích với res.data của API /chat/showdetail.
 */
export interface IConversationDetailRes {
  conversation: Partial<IConversation>;
  message: {
    items?: any[]; // Danh sách tin nhắn (thô từ BE)
    result?: any[]; // Fallback format elastic
    next?: any; // Cursor phân trang
  };
  customer: ICustomerDetail;
  feature?: any[]; // Danh sách tính năng được bật
  post?: any; // Thông tin bài viết (nếu là comment)
  postMsg?: any[]; // Danh sách bài viết liên quan trong tin nhắn
  customerGroup?: any[]; // Danh sách thành viên nhóm (ZLPS)
  zaloStickers?: any[]; // Stickers zalo
}

/**
 * Interface Truy vấn khi Lọc (Filter Dashboard)
 * Thay cho `params = {}` vô địa chỉ cũ tại filter-conversation.service
 */
export interface IConversationFilterParam {
  keyword?: string; // Search String
  customerName?: string; // Tương thích luồng cũ: tìm theo tên/sđt
  /** Loại tìm kiếm BE: 1 = tên/SĐT/nội dung chat, 2 = ID đơn Nhanh POS. */
  typeSearch?: ConversationSearchType;
  shouldCustomerName?: boolean; // Cờ đánh dấu lọc chỉ theo customerName (logic bản cũ)
  channelIds?: number[]; // Chỉ xem các kênh
  filterPageIds?: string[]; // Tương thích luồng cũ: lọc theo page
  filterAffPageIds?: string[]; // Danh sách pageId đối tác affiliate
  tags?: string[]; // Chỉ tìm các hội thoại có Gắn mác XYZ
  tagIds?: string[]; // Tương thích luồng cũ: lọc theo tag
  status?: number; // 1 = chỉ hiện Chat chưa đọc (BE lọc chưa đọc với mọi giá trị status gửi lên)
  type?: number; // Tương thích luồng cũ: inbox/comment/group
  hasReply?: boolean; // Lọc hội thoại đã/chưa trả lời
  fromAds?: boolean; // Lọc hội thoại đến từ quảng cáo
  hasPhone?: boolean; // Chỉ hiện Chat có SĐT
  searchContent?: number | string; // Tương thích cờ tìm theo nội dung cũ
  cid?: string; // Mở thẳng hội thoại theo ID (từ URL)
  psid?: string; // Tìm theo fanpageUserId (FE13 parity)
  divisionStaffId?: string; // Lọc theo Chat đã gán cho 1 user/nhân viên
  assigneeIds?: string[]; // Tương thích luồng chia hội thoại
  assignedGroupId?: string; // Tương thích filter theo nhóm được gán
  updatedTime?: string; // "fromUnix,toUnix"
  createdTime?: string; // "fromUnix,toUnix"
  reportFilter?: boolean; // Cờ truy vấn hội thoại từ báo cáo bài viết
  newConversation?: boolean; // Cờ lọc hội thoại mới từ báo cáo
  oldConversation?: boolean; // Cờ lọc hội thoại cũ từ báo cáo
  interactTime?: string; // "fromUnix,toUnix" — lọc hội thoại có tương tác (theo thời gian tin nhắn) từ báo cáo tương tác
  interactGroup?: string; // Báo cáo tương tác: newCustomer | oldCustomer | phone (không có = Tổng khách / theo type)
  interactByPage?: number; // 1 = mở từ tab Theo kênh của báo cáo tương tác (cách tính khách mới)
  pagePhone?: string; // Báo cáo Trang, cột SĐT: day = SĐT trong ngày | new = SĐT mới (dùng kèm reportFilter + pagePhoneTime)
  pagePhoneTime?: string; // "fromUnix,toUnix" — thời gian của dòng báo cáo Trang
  pagePhoneHour?: string; // 0–23 (GMT+7) — dòng của chế độ nhóm theo khung giờ
  postIds?: string[]; // Lọc theo danh sách bài viết
  hasDraftOrder?: boolean; // Lọc hội thoại có đơn nháp
  chatbotStatus?: number | string; // Lọc theo trạng thái BizAI (CHATBOT_STATUS values)
  adIds?: string | string[]; // Lọc hội thoại theo ID quảng cáo
  after?: string; // Cursor phân trang
  /** Yêu cầu BE trả totalItems; chỉ gửi ở trang đầu để tránh đếm lại khi cuộn. */
  withTotalItems?: boolean;
  dateRange?: { from: number, to: number };
  dataBussiness?: string; // Dữ liệu phân quyền page theo business (JSON string)
  sort?: number; // Kiểu sắp xếp hội thoại: 1=unread first, 2=by time, 3=no reply first
  // --- Comment filter extras ---
  isFromLive?: number | null; // 1 = từ livestream, 0 = không từ livestream
  isCreatedOrder?: number | null; // 1 = đã tạo đơn hàng
  isSentPrivateMessage?: number | null; // 0 = chưa gửi tin nhắn riêng
  // --- Tag filter extras ---
  noTag?: number | null; // 1 = lọc hội thoại không có nhãn
  totalTag?: number | null; // 1 = có một nhãn bất kỳ, số lượng = có đủ các nhãn
  [key: string]: any;
}

/**
 * Trạng thái lọc của hội thoại do BE đọc lại từ document SAU KHI ghi, gửi kèm event realtime
 * `conversation:changes` ở key `filterState` (cùng cấp `conversation`, `messages`).
 * `conversation` trong payload chỉ là phần dữ liệu event ghi (webhook luôn gửi hasPhone=false,
 * không gửi tagIds...), còn filterState phản ánh đúng document: field document không có thì BE
 * không gửi, FE hiểu là "không có giá trị" giống cách Elasticsearch lọc.
 */
export interface IConversationFilterState {
  tagIds?: string[] | null; // Không có nhãn: [] hoặc không gửi
  status?: number | string | null; // 1 = chưa đọc, 2 = đã đọc
  hasPhone?: boolean | null;
  hasReply?: boolean | null;
  hasDraftOrder?: boolean | null;
  chatbotStatus?: number | string | null;
  assigneeIds?: string[] | null;
  assignedGroupId?: string | null;
  fanpagePostId?: string | null;
  adIds?: string[] | null;
  fromAdsId?: string | null;
}

/** Giá trị `typeSearch` được gửi tới API /chat/conversations. */
export const CONVERSATION_SEARCH_TYPE = {
  CUSTOMER: 1,
  NHANH_POS_ORDER: 2,
} as const;

export type ConversationSearchType = typeof CONVERSATION_SEARCH_TYPE[keyof typeof CONVERSATION_SEARCH_TYPE];

/** Interface response data từ API /chat/conversations */
/** Kết quả tìm kiếm kết hợp (hội thoại + tin nhắn) — BE trả khi có customerName/keyword */
export interface ISearchMessage {
  id: string;
  conversation?: Partial<IConversation>;
  /** BE trả conversationId để FE lookup full conversation từ list hiện có */
  conversationId?: string;
  fanpageUserId?: string;
  senderName?: string;
  message?: string;
  createdTime?: number;
  updatedTime?: number;
}

/** Tab đang hiển thị trong panel kết quả tìm kiếm */
export type TSearchTab = 'all' | 'conversations' | 'messages';

export interface ISearchResults {
  conversations: IConversation[];
  messages: ISearchMessage[];
  totalMessages: number;
  keyword?: string;
}

export interface IConversationListResponse {
  items?: IConversation[];
  result?: IConversation[]; // Phòng trường hợp BE chuyển sang format mới
  next?: any;
  /** Tổng số hội thoại khớp điều kiện lọc, được BE trả kèm response phân trang. */
  totalItems?: number;
  fanpageAllIds?: string[];
  roleByBusinessIds?: Record<string, any>;
  reassignAfter?: number;
  searchedData?: {
    allMessages?: ISearchMessage[];
    totalMessages?: number;
    conversationIds?: string[];
    /** Customer IDs matched by a phone-number search (FE13 contract). */
    fanpageUserIds?: Array<string | number>;
  };
  leaderGroupIds?: string[];
  leaderPageIds?: string[];
  modeByBusiness?: any[];
  msgSplit?: string;
  pageManageIds?: string[];
  statusSplit?: boolean;
}
